import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const NigeriaPage = () => {
  const nigeriaData = {
    name: 'Nigeria',
    region: 'Africa',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Nigeria | NAFDAC | TheApostrophe',
    metaDescription: 'Understand how to register pharma and nutraceutical products in Nigeria. NAFDAC requirements, CTD dossier format, dietary supplement rules, labelling, and regulatory pathways for Africa\'s largest pharmaceutical market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Nigeria',
      subtitle: 'A large, import-dependent West African pharmaceutical market regulated by NAFDAC, with strong demand for essential medicines, chronic therapies, and regulated dietary supplements.',
      cta: 'Start Your Nigeria Registration',
      secondaryCta: null
    },
    
    atAGlance: [
      { label: 'Population', value: '~227 million' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 75–85' },
      { label: 'Health Expenditure (% of GDP)', value: '~3.5–4%' },
      { label: 'Urbanisation', value: '~55% urban, 45% rural' },
      { label: 'Life Expectancy', value: '55 (men), 56 (women)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Africa\'s Largest Pharma Import Market',
      stats: [
        'Nigeria is the largest pharmaceutical market in Africa by population and remains heavily dependent on imported finished formulations',
        'Finished pharmaceuticals account for a major share of national consumption',
        'Local manufacturing exists but is concentrated in basic formulations and limited GMP scale',
        'Advanced dosage forms, injectables, oncology, and complex generics are largely imported',
        'India is one of Nigeria\'s top pharmaceutical suppliers, particularly for generics and essential medicines',
        'Nutraceuticals and supplements are regulated and gaining acceptance, especially in urban centres'
      ],
      opportunities: [
        'Large private hospital and clinic network across Lagos, Abuja, Port Harcourt, Ibadan',
        'Expanding retail pharmacy chains and distributor networks',
        'High acceptance of Indian generics on quality–price balance',
        'Government and donor-funded procurement for essential medicines',
        'Growing nutraceutical demand among middle-income urban consumers'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: What Drives Demand in Nigeria',
      subtitle: 'Nigeria faces a dual burden of infectious diseases and rapidly rising non-communicable diseases (NCDs).',
      categories: [
        {
          name: 'Infectious Diseases',
          items: [
            'Malaria remains endemic nationwide',
            'TB burden remains significant',
            'HIV prevalence continues to require sustained treatment supply',
            'High-demand categories: Antimalarials, antibiotics, antiretrovirals, ORS, injectables, paediatric formulations'
          ]
        },
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Rapid growth in hypertension and cardiovascular disorders',
            'Rising diabetes prevalence driven by urbanisation and lifestyle changes',
            'Maternal anaemia and micronutrient deficiencies remain common',
            'High-demand categories: Antihypertensives, antidiabetics, analgesics, iron, calcium, prenatal supplements'
          ]
        },
        {
          name: 'Nutraceutical Opportunities',
          items: [
            'Maternal and prenatal supplements',
            'Paediatric multivitamins',
            'Vitamin C, zinc, immunity support products',
            'Probiotics and metabolic health supplements'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'National Agency for Food and Drug Administration and Control (NAFDAC)',
      description: 'NAFDAC operates under the Federal Ministry of Health and regulates pharmaceuticals, herbal medicines, medical devices, and dietary supplements. Nigeria maintains a strict pre-market registration system with strong emphasis on documentation, labelling accuracy, and importer accountability.',
      pharma: {
        timeline: 'Varies by product category, dossier quality, and GMP verification',
        requirements: [
          'Nigeria-specific administrative dossier',
          'Technical quality documentation (CTD-aligned structure commonly used)',
          'GMP certificate from manufacturing site',
          'Stability data suitable for tropical climate',
          'Product samples for laboratory evaluation',
          'Local Marketing Authorisation Holder (MAH) mandatory',
          'Strict artwork and claims scrutiny',
          'Document-driven regulatory approach with local representation mandatory'
        ],
        insight: 'Nigeria is documentation-intensive. Clean labelling, correct claims, and aligned certificates are critical to avoid prolonged queries or rejections.'
      },
      nutra: {
        timeline: 'Varies by product category and ingredient complexity',
        requirements: [
          'Product classification & category confirmation',
          'Quantitative formulation disclosure',
          'Safety justification for ingredients',
          'COA & microbiological compliance',
          'GMP certificate submission',
          'Claims assessment (strict separation from medicinal claims)',
          'Artwork & labelling review',
          'Sample testing (where applicable)'
        ],
        insight: 'Nutraceuticals require clear separation from medicinal claims. NAFDAC scrutinizes health claims carefully — structure-function claims must be well-documented and substantiated.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharma Registration (NAFDAC)',
      steps: [
        'Appointment of a Nigeria-registered MAH',
        'Product listing & application submission',
        'Technical dossier review',
        'GMP verification (document-based; inspection may be required)',
        'Product sampling & laboratory analysis',
        'Deficiency letter handling',
        'Approval & NAFDAC registration number issuance',
        'Periodic renewals and post-approval compliance'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Dietary Supplement Registration (NAFDAC)',
      steps: [
        'Product classification & category confirmation',
        'Quantitative formulation disclosure',
        'Safety justification for ingredients',
        'COA & microbiological compliance',
        'GMP certificate submission',
        'Claims assessment (strict separation from medicinal claims)',
        'Artwork & labelling review',
        'Sample testing (where applicable)',
        'Query resolution',
        'Market authorisation issuance'
      ]
    },
    
    requiredDocs: {
      'Pharma': [
        'Administrative application forms',
        'Quality and technical documentation',
        'GMP certificate',
        'Certificate of Pharmaceutical Product (where applicable)',
        'Stability data (tropical conditions)',
        'API documentation',
        'Finished product specifications & COA',
        'Labels and inserts (English mandatory)'
      ],
      'Nutraceuticals': [
        'Full quantitative formula',
        'Finished product & ingredient COA',
        'GMP certificate',
        'Safety justification',
        'Claims rationale',
        'Packaging artwork compliant with NAFDAC rules',
        'Certificate of Free Sale (when applicable)'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Nigeria Should Be Part of Your Expansion Strategy',
      reasons: [
        'Africa\'s largest population and medicine consumption base',
        'Heavy reliance on imported finished pharmaceuticals',
        'Strong and growing private healthcare sector',
        'High demand for essential and chronic therapies',
        'Expanding nutraceutical consumption in urban centres',
        'Long-term volume-driven market for compliant suppliers'
      ],
      bestPortfolio: [
        'Pharma: Antimalarials, antibiotics, antihypertensives, antidiabetics, injectables, ORS, paediatrics',
        'Nutra: Maternal supplements, paediatric multivitamins, vitamin C, zinc, probiotics, metabolic health products'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Supports Nigeria Submissions',
      services: [
        'Product–market suitability assessment',
        'NAFDAC-aligned dossier preparation',
        'Artwork & label compliance review',
        'Nutraceutical classification and safety documentation',
        'Importer & MAH coordination',
        'Deficiency and query response management',
        'Post-approval lifecycle support (renewals, variations)'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority regulates pharmaceuticals in Nigeria?',
        answer: 'The National Agency for Food and Drug Administration and Control (NAFDAC), operating under the Federal Ministry of Health, is responsible for regulating pharmaceuticals, herbal medicines, medical devices, and dietary supplements in Nigeria.'
      },
      {
        question: 'Is local representation mandatory?',
        answer: 'Yes, foreign manufacturers must appoint a Nigeria-registered Marketing Authorisation Holder (MAH) for product registration. The MAH is responsible for regulatory compliance, post-market surveillance, and liaison with NAFDAC.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Yes, GMP certificates from recognized Indian regulatory authorities are generally accepted by NAFDAC. However, NAFDAC may conduct document-based GMP verification or request facility inspection for certain product categories.'
      },
      {
        question: 'Do supplements require registration?',
        answer: 'Yes, dietary supplements and nutraceuticals require NAFDAC registration before they can be marketed in Nigeria. They follow a separate registration pathway with specific requirements for safety documentation and claims substantiation.'
      },
      {
        question: 'What language is required on labels?',
        answer: 'English is mandatory on all pharmaceutical and nutraceutical product labels in Nigeria. Labels must include product name, active ingredients, dosage, storage conditions, batch number, expiry date, manufacturer details, and NAFDAC registration number.'
      }
    ]
  };

  return <CountryTemplate countryData={nigeriaData} />;
};
